import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { CATALOGS, type Locale } from './catalog';

const STORAGE_KEY = 'rr.tenant.locale';

/** Values substituted into `{{name}}` placeholders (W14). */
export type TranslateVars = Record<string, string | number>;

/**
 * `t(key)` · `t(key, vars)` · `t(key, fallback)` · `t(key, fallback, vars)`.
 * Missing keys fall back locale → en → explicit fallback → raw key, never throw.
 */
export type Translate = (key: string, fallbackOrVars?: string | TranslateVars, vars?: TranslateVars) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && stored in CATALOGS) return stored as Locale;
  // Fall back to the browser language if it's one we support.
  const browser = window.navigator.language.slice(0, 2);
  if (browser in CATALOGS) return browser as Locale;
  return 'en';
}

/** Replace `{{name}}` tokens; unknown tokens are left verbatim. */
export function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
}

/**
 * The catalog is keyed by bare language codes; `Intl` wants a BCP-47 tag. Every
 * language this product ships is a South African official language, so the
 * region is unambiguous, and it is the region that decides whether a customer
 * sees "R5 000,00" or "ZAR 5000.00", and "5 Sep 2026" or "9/5/2026".
 */
function bcp47(locale: Locale): string {
  return `${locale}-ZA`;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  /*
   * Push the choice into the sdk-utils store. Everything in `sdk-utils/format`
   * and `sdk-utils/date` resolves its locale from there
   * (`sdk-utils/src/date/localize.ts:getResolvedLocale`), so without this the
   * language picker changed the copy while every date, number and currency on
   * the page stayed on `navigator.language`.
   */
  useEffect(() => {
    useAppStore.getState().setLocale(bcp47(locale));
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { window.localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  const t = useCallback<Translate>(
    (key, fallbackOrVars, vars) => {
      const fallback = typeof fallbackOrVars === 'string' ? fallbackOrVars : undefined;
      const substitutions = typeof fallbackOrVars === 'object' ? fallbackOrVars : vars;
      const catalog = CATALOGS[locale];
      // Missing keys fall back to English, then to the explicit fallback,
      // then to the raw key: never throws.
      const raw = catalog[key] ?? CATALOGS.en[key] ?? fallback ?? key;
      return interpolate(raw, substitutions);
    },
    [locale]
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Convenience hook for the common case: just the translate function. */
export function useT(): Translate {
  return useI18n().t;
}
